/* eslint-disable no-nested-ternary */
/* eslint-disable max-len */
/* eslint-disable no-bitwise */
/* eslint-disable react/prop-types */
/* eslint-disable import/no-extraneous-dependencies */
import React, {
  useCallback, useContext, useEffect, useState,
} from 'react';
import PropTypes from 'prop-types';
import { useLocation, useHistory, Link } from 'react-router-dom';
import { Search, Plus, Upload } from 'lucide-react';
import NewMemberModal from './NewMemberModal';
import BulkImportMembersModal from './BulkImportMembersModal';
import { sendRequest } from '../../api/shootAPI';
import API from '../../api/endpoints';
import { UserContext } from '../../contexts/User';
import './members.css';
import DeleteMemberConfirmationModal from './DeleteMemberConfirmationModal';
import { Navbar, Sidebar } from '../../components';
import EditMemberModal from './EditMemberModal';
import useDebounce from '../../hooks/useDebounce';
import DataTable from '../../components/DataTable';
import StatusPill from '../../components/StatusPill';
import UserEditColumn from '../../components/table/UserEditColumn';
import SetupAlertModal from './SetupAlertModal';
import { formatDateWithTimezone } from '../../utils/time';
import { STYLED_SELECT_CLASS, STYLED_SELECT_STYLE } from '../../utils/styledSelect';

const STATUS_TONE = {
  Active: 'success',
  Invited: 'warning',
  Deactivated: 'neutral',
};

const dash = <span className="text-ink-500">—</span>;

// Avatar coloring + initials — mirrors the Timesheets name cell.
const AVATAR_PALETTE = [
  { bg: '#fdebe6', fg: '#b94f3c' },
  { bg: '#fde6d2', fg: '#F19235' },
  { bg: '#eef5ea', fg: '#53A551' },
  { bg: '#e8eef7', fg: '#4062E5' },
  { bg: '#f5e6f7', fg: '#9333ea' },
];

function avatarColors(key) {
  const str = String(key || '');
  let h = 0;
  for (let i = 0; i < str.length; i += 1) h = (h * 31 + str.charCodeAt(i)) >>> 0;
  return AVATAR_PALETTE[h % AVATAR_PALETTE.length];
}

function initialsFrom(firstName, lastName, email) {
  if (firstName && lastName) return `${firstName[0]}${lastName[0]}`.toUpperCase();
  if (email) return email.slice(0, 2).toUpperCase();
  return '?';
}

function NameCell({ member }) {
  const firstName = member.user?.firstName || '';
  const lastName = member.user?.lastName || '';
  const email = member.user?.email;
  const fullName = `${firstName} ${lastName}`.trim() || email || '—';
  const c = avatarColors(email || member.id);
  return (
    <div className="flex items-center gap-3 min-w-0">
      <div
        className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
        style={{
          backgroundColor: c.bg, color: c.fg, fontSize: '11px', fontWeight: 600,
        }}
      >
        {initialsFrom(firstName, lastName, email)}
      </div>
      <div className="min-w-0">
        <Link
          to={`/profile/${member.id}`}
          onClick={(e) => e.stopPropagation()}
          className="block text-ink-900 truncate hover:text-brand-700 hover:underline"
          style={{ fontSize: 14, fontWeight: 500, lineHeight: '20px' }}
        >
          {fullName}
        </Link>
        <p className="text-ink-500 truncate" style={{ fontSize: 11, lineHeight: '16px', marginTop: 2 }}>
          {email}
        </p>
      </div>
    </div>
  );
}
NameCell.propTypes = { member: PropTypes.shape({}).isRequired };

function Members() {
  const { search } = useLocation();
  const history = useHistory();
  const query = new URLSearchParams(search);
  const page = query.get('page');
  const { activeWorkspace } = useContext(UserContext);
  const [showNewMemberModal, setShowNewMemberModal] = useState(false);
  const [showBulkImportModal, setShowBulkImportModal] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState(null);
  const [memberToEdit, setMemberToEdit] = useState(null);
  const [memberToSetupAlert, setMemberToSetupAlert] = useState(null);
  const [members, setMembers] = useState([]);
  const [reportingManagers, setReportingManagers] = useState([]);
  const [pageSize, setPageSize] = useState(10);
  const [totalCounts, setTotalCounts] = useState(0);
  const [currentPage, setCurrentPage] = useState(page ? parseInt(page, 10) : 1);
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('active'); // 'active' | 'inactive' | 'all'
  const [isLoading, setIsLoading] = useState(false);
  const [sortKey, setSortKey] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);
  const { debounce } = useDebounce();

  const getDepartments = async () => {
    if (activeWorkspace) {
      const response = await sendRequest({
        method: 'get',
        url: API.WORKSPACE.departments(activeWorkspace.id),
      });
      if (response.success) {
        setDepartments(response.data?.departments || []);
      }
    }
  };

  const getDesignations = async () => {
    if (activeWorkspace) {
      const response = await sendRequest({
        method: 'get',
        url: API.WORKSPACE.designations(activeWorkspace.id),
      });
      if (response.success) {
        setDesignations(response.data?.designations || []);
      }
    }
  };

  const handleMemberUpdate = (updatedMember) => {
    setMembers((prevMembers) => prevMembers.map((row) => {
      if (row.id === updatedMember.id) {
        return { ...row, member: { ...row.member, ...updatedMember } };
      }
      return row;
    }));
  };

  const getMembers = useCallback(async (params) => {
    if (activeWorkspace) {
      setIsLoading(true);

      const [membersResponse, projectCountsResponse] = await Promise.all([
        sendRequest({
          method: 'get',
          url: API.WORKSPACE.members(activeWorkspace.id),
          params: { ...params },
        }),
        sendRequest({
          method: 'get',
          url: API.WORKSPACE.memberProjectCounts(activeWorkspace.id),
        }),
      ]);

      if (membersResponse.success) {
        const projectCounts = projectCountsResponse.success ? projectCountsResponse.data : {};

        if (membersResponse.data?.members?.length) {
          const membersSet = new Set();
          const mappedMembers = membersResponse.data.members.reduce((acc, member) => {
            if (membersSet.has(member.id)) return acc;
            membersSet.add(member.id);

            const reportingManager = `${member.Manager?.ManagerUser?.firstName ?? ''} ${member.Manager?.ManagerUser?.lastName ?? ''}`.trim();

            acc.push({
              id: member.id,
              member,
              accountType: member.accountType,
              reportingManager,
              department: member.department,
              country: member.user?.userLocation?.country,
              designation: member.designation,
              employmentStartDate: formatDateWithTimezone(
                member.employmentStartDate,
                activeWorkspace?.timezone,
              ),
              projectCount: projectCounts.data?.[member.id] || 0,
              status: member.haveJoined ? 'Active' : member.status ? 'Invited' : 'Deactivated',
            });
            return acc;
          }, []);

          setMembers(mappedMembers);
        } else {
          setMembers([]);
        }
        setTotalCounts(membersResponse.data?.pageInfo.totalCounts ?? 0);
        setIsLoading(false);
      }
    }
  }, [activeWorkspace]);

  const getReportingMembers = useCallback(async () => {
    if (activeWorkspace) {
      const response = await sendRequest({
        method: 'get',
        url: API.WORKSPACE.reportingManagers(activeWorkspace.id),
      });
      if (response.success) {
        setReportingManagers(response.data.reportingManagers);
      }
    }
  }, []);

  const fetchResults = async () => {
    const params = {
      page: currentPage,
      name: searchQuery,
      pageSize,
      sortKey,
      sortOrder,
    };
    // 'active' → status=true, 'inactive' → status=false (deactivated), 'all' → omit
    if (statusFilter === 'active') {
      params.status = true;
    } else if (statusFilter === 'inactive') {
      params.status = false;
    }
    await getMembers(params);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSetSearch = useCallback(
    debounce((value) => {
      setSearchQuery(value);
      setCurrentPage(1);
      history.push('?page=1');
    }, 500),
    [],
  );

  const handleSearchChange = (e) => {
    setSearchInput(e.target.value);
    debouncedSetSearch(e.target.value);
  };

  const handlePageChange = (next) => {
    setCurrentPage(next);
    history.push(`?page=${next}`);
  };

  const handlePageSizeChange = (size) => {
    setCurrentPage(1);
    setPageSize(size);
  };

  const handleStatusFilterChange = (e) => {
    setStatusFilter(e.target.value);
    setCurrentPage(1);
    history.push('?page=1');
  };

  const toggleSort = (key) => {
    if (key === sortKey) {
      setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortOrder('asc');
    }
    setCurrentPage(1);
  };

  useEffect(() => {
    fetchResults();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, searchQuery, pageSize, sortKey, sortOrder, statusFilter]);

  useEffect(() => {
    getReportingMembers();
    getDepartments();
    getDesignations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columns = [
    {
      key: 'name',
      label: 'Name',
      sortable: true,
      width: 260,
      render: (r) => <NameCell member={r.member} />,
    },
    {
      key: 'accountType',
      label: 'Account Type',
      sortable: true,
      width: 150,
      render: (r) => <span className="text-ink-900">{r.accountType}</span>,
    },
    {
      key: 'reportingManager',
      label: 'Reports To',
      sortable: true,
      width: 120,
      flex: true,
      render: (r) => r.reportingManager || dash,
    },
    {
      key: 'department',
      label: 'Department',
      sortable: true,
      width: 115,
      flex: true,
      render: (r) => r.department || dash,
    },
    {
      key: 'country',
      label: 'Country',
      sortable: false,
      width: 120,
      flex: true,
      render: (r) => (r.country ? <span className="whitespace-nowrap">{r.country}</span> : dash),
    },
    {
      key: 'designation',
      label: 'Designation',
      sortable: true,
      width: 120,
      flex: true,
      render: (r) => r.designation || dash,
    },
    {
      key: 'employmentStartDate',
      label: 'Start Date',
      sortable: true,
      width: 110,
      render: (r) => r.employmentStartDate || dash,
    },
    {
      key: 'projectCount',
      label: 'Projects',
      sortable: true,
      width: 85,
      render: (r) => <span className="tabular-nums">{r.projectCount}</span>,
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      width: 95,
      render: (r) => <StatusPill tone={STATUS_TONE[r.status] || 'neutral'}>{r.status}</StatusPill>,
    },
    {
      key: 'idleTimeTrackingEnabled',
      label: 'Idle Tracking',
      sortable: true,
      width: 138,
      render: (r) => (
        <StatusPill tone={r.member.idleTimeTrackingEnabled ? 'success' : 'neutral'}>
          {r.member.idleTimeTrackingEnabled ? 'Enabled' : 'Disabled'}
        </StatusPill>
      ),
    },
    {
      key: 'manualTrackerEnabled',
      label: 'Manual Tracker',
      sortable: true,
      width: 150,
      render: (r) => (
        <StatusPill tone={r.member.manualTrackerEnabled ? 'success' : 'neutral'}>
          {r.member.manualTrackerEnabled ? 'Enabled' : 'Disabled'}
        </StatusPill>
      ),
    },
    {
      key: 'actions',
      label: '',
      sortable: false,
      width: 56,
      render: (r) => (
        <UserEditColumn
          member={r.member}
          deactivateMember={() => setMemberToDelete(r.member)}
          editMember={() => setMemberToEdit(r.member)}
          setupAlert={() => setMemberToSetupAlert(r.member)}
          onMemberUpdate={handleMemberUpdate}
        />
      ),
    },
  ];

  return (
    <div>
      {showNewMemberModal && (
        <NewMemberModal
          closeModal={() => setShowNewMemberModal(false)}
          getMembers={fetchResults}
          getReportingManagers={getReportingMembers}
          reportingManagers={reportingManagers}
          departments={departments}
          designations={designations}
        />
      )}
      {showBulkImportModal && (
        <BulkImportMembersModal
          closeModal={() => setShowBulkImportModal(false)}
          getMembers={fetchResults}
        />
      )}
      {memberToDelete && (
        <DeleteMemberConfirmationModal
          member={memberToDelete}
          getMembers={fetchResults}
          closeModal={() => setMemberToDelete(null)}
        />
      )}
      {memberToEdit && (
        <EditMemberModal
          member={memberToEdit}
          departments={departments}
          designations={designations}
          getMembers={fetchResults}
          getReportingManagers={getReportingMembers}
          closeModal={() => setMemberToEdit(null)}
          reportingManagers={reportingManagers}
        />
      )}
      {memberToSetupAlert && (
        <SetupAlertModal
          closeModal={() => setMemberToSetupAlert(null)}
          member={memberToSetupAlert}
          getMembers={fetchResults}
        />
      )}

      <Navbar />
      <Sidebar />
      <div className="screen-wrapper">
        <div className="screen-header" style={{ fontFamily: 'Inter, sans-serif' }}>
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between mb-5">
            <div className="min-w-0">
              <h1
                className="font-bold text-ink-900 leading-tight truncate text-2xl lg:text-[32px] lg:leading-10"
                data-testid="title"
              >
                Workspace Members
              </h1>
              <p className="text-sm text-ink-500 mt-1" data-testid="subtitle">
                Manage members within your workspace
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2 flex-shrink-0">
              <select
                value={statusFilter}
                onChange={handleStatusFilterChange}
                data-testid="status-filter"
                className={`${STYLED_SELECT_CLASS} min-w-[150px]`}
                style={STYLED_SELECT_STYLE}
              >
                <option value="active">Active members</option>
                <option value="inactive">Inactive members</option>
                <option value="all">All members</option>
              </select>
              <div className="relative" style={{ height: 40, width: 220 }}>
                <Search
                  size={14}
                  className="text-ink-500 pointer-events-none"
                  style={{
                    position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', zIndex: 1,
                  }}
                />
                <input
                  type="text"
                  value={searchInput}
                  onChange={handleSearchChange}
                  placeholder="Search for user"
                  data-testid="search"
                  className="w-full rounded-[8px] bg-white text-ink-900 placeholder:text-ink-500 border border-rule focus:outline-none focus:border-brand-500"
                  style={{
                    height: 40,
                    paddingLeft: 36,
                    paddingRight: 12,
                    fontSize: 14,
                    lineHeight: '20px',
                    borderStyle: 'solid',
                    boxSizing: 'border-box',
                  }}
                />
              </div>
              <button
                type="button"
                onClick={() => setShowBulkImportModal(true)}
                data-testid="import-users"
                className="inline-flex items-center gap-1.5 px-4 py-2 border border-rule rounded-[8px] text-sm font-medium text-ink-900 bg-white hover:bg-gray-50 transition whitespace-nowrap"
                style={{ borderStyle: 'solid' }}
              >
                <Upload size={16} />
                Import Users
              </button>
              <button
                type="button"
                onClick={() => setShowNewMemberModal(true)}
                data-testid="new-member"
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#53A551] text-white rounded-[8px] text-sm font-semibold hover:bg-[#458a44] transition whitespace-nowrap"
              >
                <Plus size={16} />
                New Member
              </button>
            </div>
          </div>

          <DataTable
            columns={columns}
            rows={members}
            getRowKey={(r) => r.id}
            sortKey={sortKey}
            sortDir={sortOrder}
            onSort={toggleSort}
            onRowClick={(r) => setMemberToEdit(r.member)}
            loading={isLoading}
            emptyText="No members found."
            page={currentPage}
            pageSize={pageSize}
            total={totalCounts}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            minWidth={1440}
          />
        </div>
      </div>
    </div>
  );
}

export default Members;
